import type { Lesson } from '../schema.js';

// Whist lessons: explain + quiz only, like Bridge's and backgammon's. The facts match the game as built
// (docs/proposals/whist-and-pirate-bridge.md): partners sit opposite, no auction and no dummy, the last card
// dealt is turned up and its suit is trump, a side scores the tricks above six, and a game is short (to 5,
// the default) or long (to 10). Honours scoring is not part of our Whist and is not taught.
export const WHIST_LESSONS: Lesson[] = [
  {
    id: 'whist-partners',
    slug: 'whist-partners-and-tricks',
    title: 'Whist: you and your partner',
    audienceLevel: 'beginner',
    concepts: ['whist'],
    steps: [
      {
        kind: 'explain',
        title: 'Two teams of two',
        body: 'Whist is played by four people in two teams. Your partner sits opposite you, and you win tricks together. Everyone gets 13 cards, so there are 13 tricks in a deal. There is no bidding and no dummy: you simply play your own hand and try to help your partner. The player to the left of the dealer leads the first trick.',
        narration: 'Four players, two teams. Your partner sits opposite you.',
      },
      {
        kind: 'quiz',
        title: 'Partners',
        body: 'Time for a quick check!',
        questions: [
          {
            prompt: 'Where does your partner sit?',
            choices: ['Opposite you', 'On your left', 'On your right'],
            correct: 0,
          },
          {
            prompt: 'How many tricks are there in one deal?',
            choices: ['4', '13', '52'],
            correct: 1,
          },
          {
            prompt: 'Is there any bidding in Whist?',
            choices: ['Yes, before every deal', 'No, you just play', 'Only for the dealer'],
            correct: 1,
          },
        ],
      },
    ],
  },
  {
    id: 'whist-trump',
    slug: 'whist-the-turned-up-trump',
    title: 'Whist: the turned-up trump',
    audienceLevel: 'beginner',
    concepts: ['whist'],
    steps: [
      {
        kind: 'explain',
        title: 'The last card decides',
        body: 'When the cards are dealt, the very last one, the dealer\'s, is turned face up for everyone to see. Its suit is trump for the whole deal. You must follow the suit that was led if you can. If you cannot, you may play any card, and a trump beats every card of the other suits. If more than one trump is played, the highest trump wins. If nobody plays a trump, the highest card of the suit that was led wins, and the winner leads the next trick.',
        narration: 'The last card dealt is shown. Its suit is trump for the whole deal.',
      },
      {
        kind: 'quiz',
        title: 'Trump',
        body: 'Time for a quick check!',
        questions: [
          {
            prompt: 'How is trump decided in our Whist?',
            choices: ['By the suit of the last card dealt, turned face up', 'The player to the left chooses', 'Hearts are always trump'],
            correct: 0,
          },
          {
            prompt: 'Hearts are trump and clubs are led. You have no clubs and you play a small heart. Nobody else plays a trump. Who wins?',
            choices: ['The highest club', 'You, with the heart', 'Your partner'],
            correct: 1,
          },
          {
            prompt: 'You hold a card of the suit that was led. What must you do?',
            choices: ['Play a card of that suit', 'Play a trump', 'Play any card you like'],
            correct: 0,
          },
        ],
      },
    ],
  },
  {
    id: 'whist-scoring',
    slug: 'whist-scoring-and-games',
    title: 'Whist: scoring a game',
    audienceLevel: 'beginner',
    concepts: ['whist'],
    steps: [
      {
        kind: 'explain',
        title: 'Count the tricks above six',
        body: 'After the 13 tricks, count how many each side won. The side with more than six scores one point for every trick above six: seven tricks is 1 point, nine tricks is 3 points, all thirteen is 7 points. The points add up over several deals until one side reaches the target. A short game goes to 5 points and a long game to 10. In our Whist there is no extra scoring for high trump cards, so counting tricks is all you need.',
        narration: 'Win more than six tricks and you score the extras. First to five wins a short game.',
      },
      {
        kind: 'quiz',
        title: 'Scoring',
        body: 'Time for a quick check!',
        questions: [
          {
            prompt: 'Your side wins 9 tricks. How many points do you score?',
            choices: ['9', '3', '1'],
            correct: 1,
          },
          {
            prompt: 'Your side wins 7 of the 13 tricks. How many points?',
            choices: ['1', '7', '0'],
            correct: 0,
          },
          {
            prompt: 'A short game ends when a side reaches how many points?',
            choices: ['5', '10', '13'],
            correct: 0,
          },
        ],
      },
    ],
  },
];
